import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';

export type DividerSpacing = keyof typeof theme.dime.spacing;

export interface DividerProps {
  type?: 'horizontal' | 'vertical';
  /** Spacing on both sides of the divider (top/bottom for horizontal, left/right for vertical). */
  spacing?: DividerSpacing;
  /** Overrides `spacing` above (horizontal) or left of (vertical) the divider. */
  spacingBefore?: DividerSpacing;
  /** Overrides `spacing` below (horizontal) or right of (vertical) the divider. */
  spacingAfter?: DividerSpacing;
}

type StyledDividerProps = {
  $before: DividerSpacing;
  $after: DividerSpacing;
};

const HorizontalDivider = styled.div<StyledDividerProps>`
  background-color: ${theme.dime.colors.border.static};
  height: 1px;
  width: 100%;
  margin: ${({ $before }) => theme.dime.spacing[$before]} 0
    ${({ $after }) => theme.dime.spacing[$after]};
`;

const VerticalDivider = styled.div<StyledDividerProps>`
  background-color: ${theme.dime.colors.border.static};
  width: 1px;
  height: auto;
  align-self: stretch;
  margin: 0 ${({ $after }) => theme.dime.spacing[$after]} 0
    ${({ $before }) => theme.dime.spacing[$before]};
`;

export const Divider: React.FC<DividerProps> = ({
  type = 'horizontal',
  spacing = 'm',
  spacingBefore,
  spacingAfter
}) => {
  const StyledDivider =
    type === 'horizontal' ? HorizontalDivider : VerticalDivider;

  return (
    <StyledDivider
      role="separator"
      aria-orientation={type}
      $before={spacingBefore ?? spacing}
      $after={spacingAfter ?? spacing}
    />
  );
};
